import { useCallback, useEffect, useRef, useState } from 'react';

const TICK_MS = 250;
const MS_PER_MINUTE = 60_000;

export interface CallTimer {
    isRunning: boolean;
    /** Live "mm:ss" while running - "00:00" once stopped/idle. */
    elapsedLabel: string;
    start: () => void;
    /** Stops the timer and returns the elapsed whole minutes (rounded up, min 1) - null if it wasn't running. */
    stop: () => number | null;
}

function formatElapsed(elapsedMs: number): string {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const ss = String(totalSeconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
}

/** A convenience stopwatch for the 'call' channel's Duration field - the minutes input stays independently editable either way. */
export function useCallTimer(): CallTimer {
    const [isRunning, setIsRunning] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const startedAtRef = useRef<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval>>();

    useEffect(
        () => () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        },
        []
    );

    const start = useCallback(() => {
        if (startedAtRef.current !== null) return;
        startedAtRef.current = Date.now();
        setElapsedMs(0);
        setIsRunning(true);
        intervalRef.current = setInterval(() => {
            const startedAt = startedAtRef.current;
            if (startedAt !== null) setElapsedMs(Date.now() - startedAt);
        }, TICK_MS);
    }, []);

    const stop = useCallback((): number | null => {
        const startedAt = startedAtRef.current;
        if (startedAt === null) return null;
        const finalElapsedMs = Date.now() - startedAt;
        if (intervalRef.current) clearInterval(intervalRef.current);
        startedAtRef.current = null;
        setIsRunning(false);
        setElapsedMs(0);
        return Math.max(1, Math.ceil(finalElapsedMs / MS_PER_MINUTE));
    }, []);

    return { isRunning, elapsedLabel: formatElapsed(elapsedMs), start, stop };
}
